import type {
  ScoreLandingGradeDetail,
  ScoreLandingPageResponse,
  ScoreLandingSyllabus,
} from "../score2026/scoreLandingPage.types";
import { SCORE_LANDING_PAGE_FALLBACK } from "../score2026";
import { EXAM_OPTIONS } from "./scoreRegistrationDummyData";
import type { ScoreRegistrationSelectOption } from "./scoreRegistrationInitialData";

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function toGradeValue(gradeId: number): string {
  return `grade-${gradeId}`;
}

function formatGradeLabel(gradeId: number): string {
  return `Grade ${gradeId}`;
}

export function toExamValue(examName: string): string {
  return normalizeText(examName).toLowerCase().replace(/\s+/g, "-");
}

export function parseScoreLandingPage(
  value: unknown,
): ScoreLandingPageResponse | null {
  if (!value) return null;

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as ScoreLandingPageResponse;
    } catch {
      return null;
    }
  }

  if (typeof value === "object") {
    return value as ScoreLandingPageResponse;
  }

  return null;
}

export function getScoreAboutTestDetails(
  scoreLandingPage: unknown,
): ScoreLandingGradeDetail[] {
  const parsed = parseScoreLandingPage(scoreLandingPage);
  const syllabusWidget = parsed?.orderedWidgets?.find(
    (widget) => widget.id === "syllabus",
  );

  const aboutTest = (
    syllabusWidget?.response as ScoreLandingSyllabus | undefined
  )?.aboutTest;

  if (!Array.isArray(aboutTest?.details)) return [];

  return [...aboutTest.details].sort((a, b) => a.grade - b.grade);
}

export function resolveScore2026AboutTestDetails(
  scoreLandingPage: unknown,
  fallback: ScoreLandingPageResponse = SCORE_LANDING_PAGE_FALLBACK,
): ScoreLandingGradeDetail[] {
  const parsed = parseScoreLandingPage(scoreLandingPage);
  if (
    Array.isArray(parsed?.orderedWidgets) &&
    parsed.orderedWidgets.length > 0
  ) {
    return getScoreAboutTestDetails(parsed);
  }
  return getScoreAboutTestDetails(fallback);
}

export function buildRegistrationGradeOptions(
  aboutTestDetails: ScoreLandingGradeDetail[],
  userGradeId?: number,
  userGradeName?: string,
): ScoreRegistrationSelectOption[] {
  const options = aboutTestDetails.map((detail) => ({
    value: toGradeValue(detail.grade),
    label: formatGradeLabel(detail.grade),
  }));

  if (!userGradeId || userGradeId <= 0) return options;

  const userValue = toGradeValue(userGradeId);
  if (options.some((option) => option.value === userValue)) {
    return options;
  }

  const userLabel = normalizeText(userGradeName)
    ? normalizeText(userGradeName).toLowerCase().startsWith("grade")
      ? normalizeText(userGradeName)
      : `Grade ${normalizeText(userGradeName)}`
    : formatGradeLabel(userGradeId);

  return [{ value: userValue, label: userLabel }, ...options];
}

export function buildRegistrationExamOptions(
  aboutTestDetails: ScoreLandingGradeDetail[],
  gradeValue: string,
): ScoreRegistrationSelectOption[] {
  const match = /^grade-(\d+)$/.exec(gradeValue);
  const gradeId = match?.[1] ? Number(match[1]) : NaN;
  if (!Number.isFinite(gradeId)) return [];

  const gradeDetail = aboutTestDetails.find(
    (detail) => detail.grade === gradeId,
  );
  if (!gradeDetail?.exams?.length) return [];

  return gradeDetail.exams.map((exam) => ({
    value: toExamValue(exam.exam),
    label: normalizeText(exam.exam) || "Exam",
  }));
}

export function mapProgrammeToAmbitionStream(
  programmeValue: string,
  programmeLabel?: string,
): string {
  const normalized = normalizeText(
    programmeLabel || programmeValue,
  ).toLowerCase();
  if (!normalized) return "";

  if (normalized.includes("neet") || normalized.includes("medical")) {
    return (
      EXAM_OPTIONS.find((option) => option.value === "medical")?.value ?? ""
    );
  }

  if (normalized.includes("jee") || normalized.includes("engineering")) {
    return (
      EXAM_OPTIONS.find((option) => option.value === "engineering")?.value ?? ""
    );
  }

  return (
    EXAM_OPTIONS.find((option) => option.value === "not-decided")?.value ?? ""
  );
}

export function resolveInitialGradeValue(
  aboutTestDetails: ScoreLandingGradeDetail[],
  userGradeId?: number,
): string {
  const gradeOptions = buildRegistrationGradeOptions(
    aboutTestDetails,
    userGradeId,
  );

  if (!gradeOptions.length) return "";

  const userValue =
    userGradeId && userGradeId > 0 ? toGradeValue(userGradeId) : "";
  if (userValue && gradeOptions.some((option) => option.value === userValue)) {
    return userValue;
  }

  return String(gradeOptions[0]?.value ?? "");
}

export function resolveInitialProgrammeValue(
  aboutTestDetails: ScoreLandingGradeDetail[],
  gradeValue: string,
  preferredExamName?: string,
): string {
  const examOptions = buildRegistrationExamOptions(
    aboutTestDetails,
    gradeValue,
  );
  if (!examOptions.length) return "";

  const normalizedPreferred = normalizeText(preferredExamName).toLowerCase();
  if (normalizedPreferred) {
    const matched = examOptions.find((option) => {
      const label = normalizeText(option.label).toLowerCase();
      const value = normalizeText(option.value).toLowerCase();
      return (
        label.includes(normalizedPreferred) ||
        normalizedPreferred.includes(label) ||
        value.includes(normalizedPreferred.replace(/\s+/g, "-"))
      );
    });
    if (matched) return String(matched.value);
  }

  return String(examOptions[0]?.value ?? "");
}
